import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Users } from "lucide-react";
import { formatPrice, api } from "@/lib/api";
import { PromoField } from "@/components/PromoField";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const BookingWidget = ({ listing }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [range, setRange] = useState();
  const [guests, setGuests] = useState(2);
  const [blocked, setBlocked] = useState([]);
  const [quote, setQuote] = useState(null);
  const [promo, setPromo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/listings/${listing.listing_id}/availability`)
      .then(r => setBlocked((r.data.blocked_dates || []).map(d => new Date(d + "T00:00:00"))))
      .catch(() => {});
  }, [listing.listing_id]);

  // Stable matcher list for the Calendar's `disabled` prop — avoids recreating the array
  // (and thus re-triggering Calendar memoisation) on every render.
  const disabledMatchers = useMemo(
    () => [{ before: new Date() }, ...blocked],
    [blocked]
  );

  useEffect(() => {
    if (range?.from && range?.to) {
      const ci = range.from.toISOString().slice(0, 10);
      const co = range.to.toISOString().slice(0, 10);
      api.post("/bookings/quote", { listing_id: listing.listing_id, check_in: ci, check_out: co, guests, promo_code: promo })
        .then(r => setQuote(r.data))
        .catch((e) => {
          setQuote(null);
          const msg = e.response?.data?.detail || "Dates unavailable";
          if (e.response?.status === 404 && promo) { setPromo(null); toast.error(t("detail.promo_invalid")); return; }
          // Translate "Minimum stay is N nights..." to localized hint
          const m = msg.match(/Minimum stay is (\d+)/i);
          toast.error(m ? t("detail.min_stay", { n: m[1] }) : msg);
        });
    } else {
      setQuote(null);
    }
  }, [range, guests, promo, listing.listing_id, t]);

  const applyPromo = async (code) => {
    const r = await api.post("/promo-codes/validate", { code });
    setPromo(r.data.code);
    toast.success(t("detail.promo_applied"));
  };

  const onReserve = async () => {
    if (!range?.from || !range?.to) { toast.error(t("detail.select_dates")); return; }
    setSubmitting(true);
    try {
      const ci = range.from.toISOString().slice(0, 10);
      const co = range.to.toISOString().slice(0, 10);
      const r = await api.post("/bookings", { listing_id: listing.listing_id, check_in: ci, check_out: co, guests, promo_code: promo });
      navigate(`/checkout/${r.data.booking_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = range?.from && range?.to
    ? `${range.from.toLocaleDateString()} → ${range.to.toLocaleDateString()}`
    : t("detail.select_dates");

  // Effective minimum stay: per-date Pricelabs rule for the chosen check-in, else listing default
  const minStay = (() => {
    const fallback = listing.min_stay_default || 3;
    if (!range?.from) return fallback;
    const iso = range.from.toISOString().slice(0, 10);
    return (listing.min_stay_overrides || {})[iso] || fallback;
  })();

  return (
    <div className="bg-white rounded-2xl border border-[#EAE3D9] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="booking-widget">
      <div className="flex items-baseline gap-2 mb-5">
        <span className="font-serif text-3xl text-[#2C2A29]">{formatPrice(listing.display_price ?? listing.price_per_night, listing.currency)}</span>
        <span className="text-sm text-[#8C7A6B]">{listing.display_price_is_avg ? t("listings.avg_per_night") : t("listings.per_night")}</span>
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="date-picker-btn" className="w-full text-start border border-[#EAE3D9] rounded-xl px-4 py-3 hover:border-[#C86D53] transition-colors flex items-center gap-3">
            <CalendarDays className="h-4 w-4 text-[#8C7A6B]" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B]">{t("listings.filter_dates")}</div>
              <div className="text-sm text-[#2C2A29]">{dateLabel}</div>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={1}
            disabled={disabledMatchers}
            data-testid="booking-calendar"
          />
        </PopoverContent>
      </Popover>
      <p className="text-[10px] text-[#8C7A6B] mt-1.5 ms-1" data-testid="min-stay-hint">
        {t("detail.min_stay_hint", { n: minStay })}
      </p>

      <div className="mt-3 border border-[#EAE3D9] rounded-xl px-4 py-3 flex items-center gap-3">
        <Users className="h-4 w-4 text-[#8C7A6B]" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B]">{t("listings.filter_guests")}</div>
          <select
            data-testid="guests-select"
            value={guests}
            onChange={(e) => setGuests(parseInt(e.target.value, 10))}
            className="w-full text-sm bg-transparent outline-none"
          >
            {Array.from({ length: listing.max_guests }).map((_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
        </div>
      </div>

      {quote && (
        <div className="mt-5 space-y-2 text-sm" data-testid="quote-breakdown">
          <Row label={`${formatPrice(quote.nightly_rate, quote.currency)} × ${quote.nights}`} value={formatPrice(quote.subtotal, quote.currency)} />
          <Row label={t("detail.cleaning")} value={formatPrice(quote.cleaning_fee, quote.currency)} />
          {quote.discount_amount > 0 && (
            <div className="flex justify-between text-[#3F5036]" data-testid="quote-discount-row">
              <span>{t("detail.promo_discount", { code: quote.promo_code })}</span><span>-{formatPrice(quote.discount_amount, quote.currency)}</span>
            </div>
          )}
          <div className="border-t border-[#EAE3D9] pt-3 mt-3 flex justify-between text-[#595450]">
            <span>{t("detail.total")}</span><span>{formatPrice(quote.total_due_now, quote.currency)}</span>
          </div>
          {quote.security_deposit > 0 && (
            <div className="flex justify-between text-[#8C7A6B] text-xs">
              <span>+ {t("detail.deposit")}</span><span>{formatPrice(quote.security_deposit, quote.currency)}</span>
            </div>
          )}
          <div className="border-t border-[#EAE3D9] pt-3 mt-3 flex justify-between font-medium text-[#2C2A29]">
            <span>{t("detail.total_to_authorize")}</span><span>{formatPrice((quote.total_to_authorize ?? (quote.total_due_now + (quote.security_deposit || 0))), quote.currency)}</span>
          </div>
          {quote.security_deposit > 0 && (
            <p className="text-[10px] text-[#8C7A6B] leading-relaxed mt-1">{t("detail.deposit_note")}</p>
          )}
        </div>
      )}

      <div className="mt-4">
        <PromoField applied={promo} onApply={applyPromo} onRemove={() => setPromo(null)} />
      </div>

      <button
        data-testid="reserve-btn"
        onClick={onReserve}
        disabled={submitting || !quote}
        className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "..." : t("cta.reserve")}
      </button>
    </div>
  );
};

const Row = ({ label, value, muted }) => (
  <div className={`flex justify-between ${muted ? "text-[#8C7A6B]" : "text-[#595450]"}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
